import * as S from "sanctuary";
import type { Either } from "sanctuary";

import { report } from "@/util/airbrake";
import { makeRequest } from "./makeRequest";
import type {
  CreateFetcher,
  HttpClientError,
  HttpClientFactoryOptions,
  HttpRequest,
  RequestTemplate,
  ResponseError,
} from "./types";

// The message a failed response without a reason phrase shows, used when
// nothing could be sent.
const NO_REASON_FOUND = "No reason found";

/**
 * httpClientFactory
 *
 * Makes the create<Service>Fetcher for one service, which turns a request
 * template and a response handler into a request method. A request method
 * resolves an Either and never rejects. On each call, it reads the base URL
 * from `getBaseURL`, builds the request with the template, sends it, and
 * passes the response to the handler. Every Left is an Error with `status`
 * and `body` (null when no response was read) and `service` (the service's
 * `name`), and never holds the request's headers. A failed send or a throwing
 * handler resolves the error as thrown. When there is no base URL or the
 * template throws, nothing is sent, and the Left says "No reason found" with
 * the reason in `cause`. Nothing is logged to the console; a missing base URL
 * or a throwing template is reported to Airbrake with `report` from
 * `@/util/airbrake`.
 *
 * @example
 * // fetchers.ts
 * export const createUserManagementServiceFetcher = httpClientFactory({
 *   name: "user_management_service",
 *   getBaseURL: getUserManagementEndpoint,
 * });
 *
 * // index.js
 * export const activateOktaUser = createUserManagementServiceFetcher(
 *   requestTemplates.activateOktaUser,
 *   envelopedResponse(responseSchemas.OKTA_USER)
 * );
 *
 * // An epic passes the args the template takes, token included.
 * requestEpic({
 *   requestMethod: activateOktaUser,
 *   requestArgs: { token: S.fromMaybe("")(getAuthToken()), userId },
 *   onSuccess: (res) => Rx.of(activateAdministeredUserSucceeded({ ... })),
 *   onFailure: (err) => Rx.of(activateAdministeredUserFailed({ ... })),
 * });
 */
export const httpClientFactory = ({
  name,
  getBaseURL,
}: HttpClientFactoryOptions): CreateFetcher => {
  // A Left with no response to read: status and body are null.
  const failure = (error: Error): Either<HttpClientError, never> =>
    S.Left(Object.assign(error, { status: null, body: null, service: name }));

  const withService = <T>(
    result: Either<ResponseError, T>
  ): Either<HttpClientError, T> =>
    S.isLeft(result)
      ? S.Left(Object.assign(result.value, { service: name }))
      : result;

  // The base URL and the request to send. Throws when there is no base URL, or
  // when the template throws.
  const prepare = <Args>(
    requestTemplate: RequestTemplate<Args>,
    args: Args
  ): [string, HttpRequest] => {
    const baseURL = getBaseURL();
    if (!baseURL) {
      throw new Error(`${name}: base URL is not configured`);
    }
    return [baseURL, requestTemplate(args)];
  };

  return (requestTemplate, responseHandler) => async (args) => {
    let prepared: [string, HttpRequest];
    try {
      prepared = prepare(requestTemplate, args);
    } catch (cause) {
      // The service, but nothing from the request.
      report({
        error: cause,
        context: { component: "httpClientFactory" },
        params: { service: name },
      });
      return failure(Object.assign(new Error(NO_REASON_FOUND), { cause }));
    }

    try {
      return withService(responseHandler(await makeRequest(...prepared)));
    } catch (error) {
      return failure(error instanceof Error ? error : new Error(String(error)));
    }
  };
};
